import { useMemo } from "react";

import type {
  ArrayAction,
  BasePointParameter,
  DynamicAction,
  DynamicParameter,
  FlipAction,
  FlipParameter,
  LinearParameter,
  LookupAction,
  LookupParameter,
  LookupTable,
  MoveAction,
  PolarParameter,
  RotateAction,
  RotationParameter,
  ScaleAction,
  StretchAction,
  VisibilityParameter,
  XYParameter,
} from "@dxf-ai-editor/drawing-model";

import { useT } from "../i18n";
import type { MessageKey } from "../i18n/messages";

/**
 * Parameter Sets tab (DYN-2 B.2).
 *
 * A Parameter Set is a preset AutoCAD authoring bundle — a parameter and
 * its matching action(s) created in one click. This file ships the 15
 * predefined sets covering every Tier 1 + Tier 2 parameter/action combo.
 * User-defined sets ship in a later iteration; a placeholder line at the
 * bottom tells the user so.
 *
 * The component is deliberately split out of BlockAuthoringPalette.tsx so
 * the parent stays under the 2000-LOC ceiling (CLAUDE.md size policy).
 */

export type ParameterSetId =
  | "linear_move"
  | "linear_stretch"
  | "linear_array"
  | "linear_scale"
  | "polar_move"
  | "polar_stretch"
  | "polar_scale"
  | "xy_move"
  | "xy_scale_uniform"
  | "xy_array"
  | "rotation_set"
  | "flip_set"
  | "visibility_set"
  | "lookup_set"
  | "basepoint";

export const PARAMETER_SET_IDS: readonly ParameterSetId[] = [
  "linear_move",
  "linear_stretch",
  "linear_array",
  "linear_scale",
  "polar_move",
  "polar_stretch",
  "polar_scale",
  "xy_move",
  "xy_scale_uniform",
  "xy_array",
  "rotation_set",
  "flip_set",
  "visibility_set",
  "lookup_set",
  "basepoint",
];

/**
 * Result of expanding a parameter set — one or more parameters and zero or
 * more actions that must be dispatched together. The host reducer flushes
 * them in order so the `add-dynamic-action` mutation sees the parameter
 * already present in the metadata.
 */
export interface ParameterSetExpansion {
  parameters: DynamicParameter[];
  actions: DynamicAction[];
  /**
   * Optional new lookup table to create alongside the set (only used by
   * the Lookup Set preset, which prepends an empty table the user can
   * edit afterwards in BLOOKUPTABLE).
   */
  lookupTable?: LookupTable;
}

export interface ParameterSetContext {
  /** Current parameters — used for unique-name generation. */
  parameters: readonly DynamicParameter[];
  actions: readonly DynamicAction[];
  lookupTables: readonly LookupTable[];
}

/**
 * Expand a parameter-set id into a concrete Parameter(s) + Action(s)
 * bundle with names/ids unique within the current block metadata.
 *
 * The resulting objects have sensible Tier 1/2 defaults identical to the
 * single-parameter wizard buttons in BlockAuthoringPalette; the user can
 * still edit them afterwards via the palette rows.
 */
export function expandParameterSet(
  setId: ParameterSetId,
  ctx: ParameterSetContext,
): ParameterSetExpansion {
  const uniqueParamName = uniqueNameFactory(ctx.parameters.map((p) => p.name));
  const uniqueParamId = uniqueIdFactory(
    ctx.parameters.map((p) => p.id),
    "param",
  );
  const uniqueActionName = uniqueNameFactory(ctx.actions.map((a) => a.name));
  const uniqueActionId = uniqueIdFactory(
    ctx.actions.map((a) => a.id),
    "action",
  );
  const uniqueTableId = uniqueIdFactory(
    ctx.lookupTables.map((t) => t.id),
    "lookup_table",
  );
  const uniqueTableName = uniqueNameFactory(
    ctx.lookupTables.map((t) => t.name),
  );

  switch (setId) {
    case "linear_move": {
      const parameter = makeLinear(
        uniqueParamId(),
        uniqueParamName("Distance"),
      );
      const action = makeMove(
        uniqueActionId(),
        uniqueActionName("Move"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "linear_stretch": {
      const parameter = makeLinear(
        uniqueParamId(),
        uniqueParamName("Distance"),
      );
      const action = makeStretch(
        uniqueActionId(),
        uniqueActionName("Stretch"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "linear_array": {
      const parameter = makeLinear(
        uniqueParamId(),
        uniqueParamName("Distance"),
      );
      const action = makeArray(
        uniqueActionId(),
        uniqueActionName("Array"),
        parameter.id,
        100,
        0,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "linear_scale": {
      const parameter = makeLinear(
        uniqueParamId(),
        uniqueParamName("Distance"),
      );
      const action = makeScale(
        uniqueActionId(),
        uniqueActionName("Scale"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "polar_move": {
      const parameter = makePolar(uniqueParamId(), uniqueParamName("Polar"));
      const action = makeMove(
        uniqueActionId(),
        uniqueActionName("Move"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "polar_stretch": {
      // Polar Stretch ships as a Stretch action on a Linear parameter plus
      // the Polar param itself — dedicated Polar Stretch action lands in
      // DYN-3.2. For now users pair Polar + Move (our best approximation)
      // plus a Stretch on a Linear param. Keep the Polar-only variant here
      // (Polar + Move) because Stretch requires Linear kind; DYN-3.2 will
      // replace this with the proper Polar Stretch.
      const parameter = makePolar(uniqueParamId(), uniqueParamName("Polar"));
      const linearId = uniqueParamId();
      const linear = makeLinear(linearId, uniqueParamName("Distance"));
      const move = makeMove(
        uniqueActionId(),
        uniqueActionName("Move"),
        parameter.id,
      );
      const stretch = makeStretch(
        uniqueActionId(),
        uniqueActionName("Stretch"),
        linear.id,
      );
      return { parameters: [parameter, linear], actions: [move, stretch] };
    }
    case "polar_scale": {
      const parameter = makePolar(uniqueParamId(), uniqueParamName("Polar"));
      const action = makeScale(
        uniqueActionId(),
        uniqueActionName("Scale"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "xy_move": {
      const parameter = makeXY(uniqueParamId(), uniqueParamName("XY"));
      const action = makeMove(
        uniqueActionId(),
        uniqueActionName("Move"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "xy_scale_uniform": {
      const parameter = makeXY(uniqueParamId(), uniqueParamName("XY"));
      const action = makeScale(
        uniqueActionId(),
        uniqueActionName("Scale"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "xy_array": {
      const parameter = makeXY(uniqueParamId(), uniqueParamName("XY"));
      const action = makeArray(
        uniqueActionId(),
        uniqueActionName("Array"),
        parameter.id,
        100,
        100,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "rotation_set": {
      const parameter = makeRotation(uniqueParamId(), uniqueParamName("Angle"));
      const action = makeRotate(
        uniqueActionId(),
        uniqueActionName("Rotate"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "flip_set": {
      const parameter = makeFlip(uniqueParamId(), uniqueParamName("Flip"));
      const action = makeFlipAction(
        uniqueActionId(),
        uniqueActionName("Flip"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action] };
    }
    case "visibility_set": {
      const parameter = makeVisibility(
        uniqueParamId(),
        uniqueParamName("Visibility"),
      );
      return { parameters: [parameter], actions: [] };
    }
    case "lookup_set": {
      const table: LookupTable = {
        id: uniqueTableId(),
        name: uniqueTableName("Table"),
        inputColumns: [],
        outputColumn: undefined,
        rows: [],
      };
      const parameter = makeLookup(
        uniqueParamId(),
        uniqueParamName("Lookup"),
        table.id,
      );
      const action = makeLookupAction(
        uniqueActionId(),
        uniqueActionName("Lookup"),
        parameter.id,
      );
      return { parameters: [parameter], actions: [action], lookupTable: table };
    }
    case "basepoint": {
      const parameter = makeBasePoint(
        uniqueParamId(),
        uniqueParamName("BasePoint"),
      );
      return { parameters: [parameter], actions: [] };
    }
    default: {
      const _never: never = setId;
      void _never;
      return { parameters: [], actions: [] };
    }
  }
}

/* ── Tab UI ───────────────────────────────────────────── */

export interface ParameterSetsTabProps {
  onAdd: (setId: ParameterSetId) => void;
}

export function ParameterSetsTab(props: ParameterSetsTabProps) {
  const t = useT();
  const sets = useMemo(() => PARAMETER_SET_IDS, []);
  return (
    <div data-testid="palette-parameter-sets-tab">
      <h4 style={{ margin: "4px 0 6px" }}>
        {t("authoring.param_set.heading")}
      </h4>
      <p style={{ margin: "0 0 10px", opacity: 0.75 }}>
        {t("authoring.param_set.hint")}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 4,
        }}
      >
        {sets.map((id) => (
          <button
            key={id}
            type="button"
            className="dialog-btn"
            onClick={() => props.onAdd(id)}
            data-testid={`palette-param-set-${id}`}
            style={{ textAlign: "left" }}
          >
            {t(`authoring.param_set.${id}` as MessageKey)}
          </button>
        ))}
      </div>
      <p
        style={{ margin: "10px 0 0", opacity: 0.6, fontSize: 11 }}
        data-testid="palette-param-set-user-defined-placeholder"
      >
        {t("authoring.param_set.user_defined_placeholder")}
      </p>
    </div>
  );
}

/* ── Factories ────────────────────────────────────────── */

function makeLinear(id: string, name: string): LinearParameter {
  return {
    id,
    kind: "linear",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    basePoint: { x: 0, y: 0 },
    secondPoint: { x: 100, y: 0 },
    defaultDistance: 100,
    currentDistance: 100,
    valueSet: { mode: "none", increment: 0, listValues: [] },
  };
}

function makePolar(id: string, name: string): PolarParameter {
  return {
    id,
    kind: "polar",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    basePoint: { x: 0, y: 0 },
    secondPoint: { x: 100, y: 0 },
    defaultDistance: 100,
    defaultAngle: 0,
    currentDistance: 100,
    currentAngle: 0,
    distanceValueSet: { mode: "none", increment: 0, listValues: [] },
    angleValueSet: { mode: "none", increment: 0, listValues: [] },
  };
}

function makeXY(id: string, name: string): XYParameter {
  return {
    id,
    kind: "xy",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    basePoint: { x: 0, y: 0 },
    secondPoint: { x: 100, y: 100 },
    defaultX: 100,
    defaultY: 100,
    currentX: 100,
    currentY: 100,
    xValueSet: { mode: "none", increment: 0, listValues: [] },
    yValueSet: { mode: "none", increment: 0, listValues: [] },
  };
}

function makeRotation(id: string, name: string): RotationParameter {
  return {
    id,
    kind: "rotation",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    basePoint: { x: 0, y: 0 },
    radius: 50,
    defaultAngle: 0,
    currentAngle: 0,
    angleValueSet: { mode: "none", increment: 0, listValues: [] },
  };
}

function makeFlip(id: string, name: string): FlipParameter {
  return {
    id,
    kind: "flip",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    axisStart: { x: 0, y: 0 },
    axisEnd: { x: 0, y: 100 },
    defaultValue: false,
    currentValue: false,
    flipLabel: "Flip",
    baseLabel: "Base",
  };
}

function makeVisibility(id: string, name: string): VisibilityParameter {
  const defaultStateId = "state-1";
  return {
    id,
    kind: "visibility",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    position: { x: 0, y: 0 },
    states: [{ id: defaultStateId, name: "State1", invisibleEntityIds: [] }],
    defaultStateId,
    currentStateId: defaultStateId,
  };
}

function makeBasePoint(id: string, name: string): BasePointParameter {
  return {
    id,
    kind: "basepoint",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    position: { x: 0, y: 0 },
  };
}

function makeLookup(
  id: string,
  name: string,
  tableId: string,
): LookupParameter {
  return {
    id,
    kind: "lookup",
    name,
    label: "",
    description: "",
    chainActions: false,
    showGrip: true,
    position: { x: 0, y: 0 },
    reverseLookup: false,
    lookupTableId: tableId,
  };
}

function makeMove(id: string, name: string, parameterId: string): MoveAction {
  return {
    id,
    kind: "move",
    name,
    parameterId,
    selectionEntityIds: [],
    distanceMultiplier: 1,
    angleOffset: 0,
  };
}

function makeStretch(
  id: string,
  name: string,
  parameterId: string,
): StretchAction {
  return {
    id,
    kind: "stretch",
    name,
    parameterId,
    selectionEntityIds: [],
    distanceMultiplier: 1,
    angleOffset: 0,
    crossingFrame: {
      corner1: { x: 0, y: -10 },
      corner2: { x: 100, y: 10 },
    },
    stretchDirection: 0,
  };
}

function makeArray(
  id: string,
  name: string,
  parameterId: string,
  columnSpacing: number,
  rowSpacing: number,
): ArrayAction {
  return {
    id,
    kind: "array",
    name,
    parameterId,
    selectionEntityIds: [],
    columnSpacing,
    rowSpacing,
  };
}

function makeScale(id: string, name: string, parameterId: string): ScaleAction {
  return {
    id,
    kind: "scale",
    name,
    parameterId,
    selectionEntityIds: [],
    scaleType: "uniform",
    scaleMultiplier: 1,
  };
}

function makeRotate(
  id: string,
  name: string,
  parameterId: string,
): RotateAction {
  return {
    id,
    kind: "rotate",
    name,
    parameterId,
    selectionEntityIds: [],
    angleMultiplier: 1,
  };
}

function makeFlipAction(
  id: string,
  name: string,
  parameterId: string,
): FlipAction {
  return {
    id,
    kind: "flip",
    name,
    parameterId,
    selectionEntityIds: [],
  };
}

function makeLookupAction(
  id: string,
  name: string,
  parameterId: string,
): LookupAction {
  return {
    id,
    kind: "lookup",
    name,
    parameterId,
    selectionEntityIds: [],
  };
}

/* ── Name / id helpers ────────────────────────────────── */

function uniqueNameFactory(
  existing: Iterable<string>,
): (prefix: string) => string {
  const used = new Set<string>(existing);
  return function next(prefix: string): string {
    let i = 1;
    // Scan from the lowest free suffix so repeated calls don't collide.
    while (used.has(`${prefix}${i}`)) i += 1;
    const name = `${prefix}${i}`;
    used.add(name);
    return name;
  };
}

function uniqueIdFactory(
  existing: Iterable<string>,
  prefix: string,
): () => string {
  const used = new Set<string>(existing);
  return function next(): string {
    let i = used.size + 1;
    while (used.has(`${prefix}-${i}`)) i += 1;
    const id = `${prefix}-${i}`;
    used.add(id);
    return id;
  };
}
